/**
 * Multi-campaign library. Each entry is a book/arc the player can open from the hub.
 */
import type { MissionDef } from "./types";
import { CAMPAIGN_MISSIONS, GIDEON_MISSIONS } from "./missions";
import { CAMPAIGN_CHAPTER } from "./story";
import { GIDEON_CHAPTER } from "./gideon/story";

export type CampaignStatus = "playable" | "coming_soon";

export interface CampaignDef {
  id: string;
  /** Short label on the library card */
  title: string;
  kicker: string;
  lead: string;
  attribution: string;
  status: CampaignStatus;
  /** Stage count when known (playable or announced) */
  stageCount?: number;
  /** Teaching focus for the library card */
  focus?: string;
  /** Playable campaigns supply the mission ladder */
  missions?: readonly MissionDef[];
  /** Signature verse id (finale grant), when known */
  signatureVerseId?: string;
  finaleMissionId?: string;
  /**
   * Campaign identity art under `public/art/story/…`
   * (library detail cover — not a stage panel).
   */
  coverArt?: string;
}

/** Rebuild the Wall — Nehemiah. */
export const CAMPAIGN_NEHEMIAH: CampaignDef = {
  id: CAMPAIGN_CHAPTER.id,
  title: CAMPAIGN_CHAPTER.title,
  kicker: CAMPAIGN_CHAPTER.kicker,
  lead: CAMPAIGN_CHAPTER.lead,
  attribution: CAMPAIGN_CHAPTER.attribution,
  status: "playable",
  stageCount: CAMPAIGN_MISSIONS.length,
  focus: "Fear · Despair · hold the wall",
  missions: CAMPAIGN_MISSIONS,
  signatureVerseId: CAMPAIGN_CHAPTER.signatureVerseId,
  finaleMissionId: CAMPAIGN_CHAPTER.finaleMissionId,
  /** Dedicated library cover plate (story-art: campaign-cover) */
  coverArt: "neh-wall/campaign-cover.webp",
};

/**
 * The Three Hundred — Judges 6–7.
 * God meets the winepress question and proves presence with three hundred.
 */
export const CAMPAIGN_GIDEON: CampaignDef = {
  id: GIDEON_CHAPTER.id,
  title: GIDEON_CHAPTER.title,
  kicker: GIDEON_CHAPTER.kicker,
  lead: GIDEON_CHAPTER.lead,
  attribution: GIDEON_CHAPTER.attribution,
  status: "playable",
  stageCount: GIDEON_MISSIONS.length,
  focus: "Doubt · Faith · Gather · three hundred",
  missions: GIDEON_MISSIONS,
  signatureVerseId: GIDEON_CHAPTER.signatureVerseId,
  finaleMissionId: GIDEON_CHAPTER.finaleMissionId,
  /** Dedicated library cover (story-art: gideon-campaign-cover) */
  coverArt: "gideon-300/gideon-campaign-cover.webp",
};

/** Ordered library (first playable is the default open). */
export const CAMPAIGNS: readonly CampaignDef[] = [
  CAMPAIGN_NEHEMIAH,
  CAMPAIGN_GIDEON,
];

/** Resolve which campaign owns a mission id. */
export function campaignForMissionId(
  missionId: string,
): CampaignDef | undefined {
  for (const c of CAMPAIGNS) {
    if (c.missions?.some((m) => m.id === missionId)) return c;
  }
  return undefined;
}

export function campaignById(id: string): CampaignDef | undefined {
  return CAMPAIGNS.find((c) => c.id === id);
}

export function defaultCampaignId(): string {
  return (
    CAMPAIGNS.find((c) => c.status === "playable")?.id ?? CAMPAIGNS[0]!.id
  );
}
